/*
 * Copyright 2026 Oddpods
 *
 * This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at https://mozilla.org/MPL/2.0/.
 *
 * See https://github.com/oddpods/cauce/blob/main/LICENSE
 */
package studio.oddpods.cauce.core.data.repositoryImpl.cauce

import io.github.jan.supabase.SupabaseClient
import io.github.jan.supabase.postgrest.postgrest
import io.github.jan.supabase.postgrest.rpc
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.filterNotNull
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.onStart
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.put
import studio.oddpods.cauce.core.data.repository.cauce.DashboardRepository
import studio.oddpods.cauce.core.data.repository.cauce.SessionRepository
import studio.oddpods.cauce.core.data.util.BusinessError
import studio.oddpods.cauce.core.data.util.safeCall
import studio.oddpods.cauce.core.data.util.safeThrow
import studio.oddpods.cauce.core.model.session.CreateSessionRequest
import studio.oddpods.cauce.core.model.session.PaymentStatus
import studio.oddpods.cauce.core.model.session.Session
import studio.oddpods.cauce.core.model.session.SessionStatus
import studio.oddpods.cauce.core.model.session.UpdateSessionRequest
import kotlin.time.Clock

class SessionRepositoryImpl(
    private val supabase: SupabaseClient,
    private val dashboardRepository: DashboardRepository,
) : SessionRepository {

    private val allSessions = MutableStateFlow<List<Session>?>(null)

    override fun getSessionsByPatient(patientId: String): Flow<List<Session>> =
        allSessions
            .onStart { if (allSessions.value == null) fetchAll() }
            .filterNotNull()
            .map { list -> list.filter { it.patientId == patientId } }

    override fun getPendingConfirmations(): Flow<List<Session>> {
        val today = Clock.System.now()
            .toLocalDateTime(TimeZone.currentSystemDefault())
            .date
            .toString()
        return allSessions
            .onStart { if (allSessions.value == null) fetchAll() }
            .filterNotNull()
            .map { list ->
                list.filter {
                    it.sessionStatus == SessionStatus.SCHEDULED && it.sessionDate <= today
                }
            }
    }

    override suspend fun getSession(sessionId: String): Result<Session> = safeCall {
        allSessions.value?.firstOrNull { it.id == sessionId }
            ?: supabase.postgrest.from("sessions").select {
                filter { eq("id", sessionId) }
            }.decodeSingle<Session>()
    }

    override suspend fun createSession(request: CreateSessionRequest): Result<Session> = safeCall {
        val withinLimits = supabase.postgrest.rpc(
            "check_tier_limits",
            buildJsonObject { put("resource_type", "sessions") },
        ).decodeAs<Boolean>()

        if (!withinLimits) throw BusinessError.LimitReached("sessions")

        val inserted = supabase.postgrest.from("sessions").insert(request) {
            select()
        }.decodeSingle<Session>()
        fetchAll()
        dashboardRepository.refresh().getOrNull()
        inserted
    }

    override suspend fun updateSession(sessionId: String, request: UpdateSessionRequest): Result<Session> = safeCall {
        val updated = supabase.postgrest.from("sessions").update({
            set("session_date", request.sessionDate)
            set("fee", request.fee)
            set("session_type", request.sessionType)
        }) {
            filter { eq("id", sessionId) }
            select()
        }.decodeSingle<Session>()
        fetchAll()
        dashboardRepository.refresh().getOrNull()
        updated
    }

    override suspend fun confirmSession(sessionId: String): Result<Unit> = safeCall {
        supabase.postgrest.from("sessions").update({
            set("session_status", SessionStatus.CONFIRMED.name)
        }) {
            filter { eq("id", sessionId) }
        }
        fetchAll()
        dashboardRepository.refresh().getOrNull()
        Unit
    }

    override suspend fun cancelSession(sessionId: String): Result<Unit> = safeCall {
        supabase.postgrest.from("sessions").update({
            set("session_status", SessionStatus.CANCELLED.name)
        }) {
            filter { eq("id", sessionId) }
        }
        fetchAll()
        dashboardRepository.refresh().getOrNull()
        Unit
    }

    override suspend fun deleteSession(sessionId: String): Result<Unit> = safeCall {
        supabase.postgrest.from("sessions").delete {
            filter { eq("id", sessionId) }
        }
        fetchAll()
        dashboardRepository.refresh().getOrNull()
        Unit
    }

    override suspend fun waiveUnpaidByPatient(patientId: String): Result<Unit> = safeCall {
        supabase.postgrest.from("sessions").update({
            set("payment_status", PaymentStatus.WAIVED.name)
            set("amount_paid", 0)
        }) {
            filter {
                eq("patient_id", patientId)
                isIn("payment_status", listOf(PaymentStatus.PENDING.name, PaymentStatus.PARTIAL.name))
                eq("session_status", SessionStatus.CONFIRMED.name)
            }
        }
        fetchAll()
        dashboardRepository.refresh().getOrNull()
        Unit
    }

    override suspend fun refresh(): Result<Unit> = safeCall {
        if (allSessions.value != null) fetchAll()
    }

    private suspend fun fetchAll() = safeThrow {
        allSessions.value = supabase.postgrest.from("sessions").select {
            order("session_date", io.github.jan.supabase.postgrest.query.Order.DESCENDING)
        }.decodeList<Session>()
    }
}
